/**
 * Pure segment-boundary decision. Given frame timestamps (µs, monotonic) it says which frames must
 * be encoded as keyframes so every segment starts on one. Boundaries are on a fixed grid anchored
 * at the first frame, so a late frame does not shift every later boundary.
 */

export interface BoundaryState {
  segmentUs: number;
  /** Timestamp at or after which the next frame starts a new segment; null before the first frame. */
  nextBoundaryUs: number | null;
  /** Set by `requestBoundary` (track swap, re-configuration): the next frame starts a segment. */
  forceNext: boolean;
}

export function createBoundaryState(segmentMs: number): BoundaryState {
  return { segmentUs: segmentMs * 1000, nextBoundaryUs: null, forceNext: false };
}

/** Marks that the next frame must start a new segment regardless of the grid. */
export function requestBoundary(state: BoundaryState): void {
  state.forceNext = true;
}

/** Returns true when the frame at `timestampUs` starts a new segment, and advances the state. */
export function decideBoundary(state: BoundaryState, timestampUs: number): boolean {
  if (state.nextBoundaryUs === null || state.forceNext) {
    // First frame, or a forced restart: re-anchor the grid here.
    state.forceNext = false;
    state.nextBoundaryUs = timestampUs + state.segmentUs;
    return true;
  }
  if (timestampUs < state.nextBoundaryUs) return false;
  state.nextBoundaryUs += state.segmentUs;
  // After a long gap (stall, tab throttling) skip missed grid points instead of emitting a burst.
  if (state.nextBoundaryUs <= timestampUs) state.nextBoundaryUs = timestampUs + state.segmentUs;
  return true;
}

/** Clock the default seq base counts in, whatever the segment length (see defaultSeqBase). */
export const SEQ_CLOCK_MS = 1000;

/**
 * Wall-clock part of the default first `seq` of a run: the current second since the epoch, plus
 * one. Counting in seconds for 1 s and 2 s segments alike keeps seq increasing across runs that
 * switch segment length.
 *
 * The clock alone cannot keep two runs apart on an immediate restart: a run of E ms may emit
 * floor(E / 1000) + 1 segments, one more than the seconds that elapsed. The segmenter therefore also
 * starts above the highest seq it has emitted in this page (see `nextDefaultSeqBase`). Across a page
 * reload, any gap of at least one second adds the missing tick.
 */
export function defaultSeqBase(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / SEQ_CLOCK_MS) + 1;
}

let highestSeqEmitted = -1;

/** Records an emitted seq so that later runs in this page start above it. */
export function noteSeqEmitted(seq: number): void {
  if (seq > highestSeqEmitted) highestSeqEmitted = seq;
}

/** Default first seq of a run: the wall-clock base, but never at or below a seq already emitted here. */
export function nextDefaultSeqBase(nowMs: number = Date.now(), cadenceMs: 200 | 1000 = 1000): number {
  return Math.max(Math.floor(nowMs / cadenceMs) + 1, highestSeqEmitted + 1);
}
